/**
 * Helpers wrapping the Pay Operations amendment endpoints. Centralizes the
 * preview/execute contract so the page-level code stays focused on UX.
 *
 * Plan ADR-4 / codex #1: preview returns a server-persisted token; execute
 * sends the token plus minimal confirmation extras — never a competing draft.
 */

import { apiClient, API_PATHS } from '@/services/api';

export interface PreviewRequest {
  action:
    'completeManually' | 'cancelManually' | 'forceCompleteManually' | 'refundAndCancelManually';
  draft: Record<string, unknown>;
  reason: string;
  reasonCategory?: string;
}

export interface PreviewResponse {
  previewToken: string;
  diff: Record<string, { before: unknown; after: unknown }>;
  totals: { beforeTotal: number | null; afterTotal: number | null; currency: string };
  stateTransition: { from: string; to: string };
  warnings: string[];
  blockers: string[];
  mappedDto: Record<string, unknown>;
  expiresAt: string;
}

export interface ExecuteRequest {
  action: PreviewRequest['action'];
  previewToken: string;
  reasonCategory?: string;
  confirmations?: Record<string, unknown>;
}

export async function runPreview(
  orderUuid: string,
  body: PreviewRequest,
): Promise<PreviewResponse> {
  const { data } = await apiClient.rpIntegration.post(
    API_PATHS.rpIntegration.payOperations.orderAmendments.preview(orderUuid),
    body,
  );
  return data;
}

export async function runExecute(
  orderUuid: string,
  body: ExecuteRequest,
): Promise<{
  success: boolean;
  partial: boolean;
  stale: boolean;
  attempt: Record<string, unknown>;
  message?: string;
}> {
  const { data } = await apiClient.rpIntegration.post(
    API_PATHS.rpIntegration.payOperations.orderAmendments.execute(orderUuid),
    body,
  );
  return data;
}
